import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  ReactNode,
  useCallback,
  useEffect,
} from "react";
import { useWebSocket, WebSocketMessage } from "@/hooks/useWebSocket";

interface Notification {
  id: string;
  status: string;
  type: string;
  message: string;
  severity: "info" | "warning" | "error" | "success";
  audience: string;
  actionRequired: boolean;
  actionLink: string | null;
}
interface WebSocketContextValue {
  isConnected: boolean;
  sendMessage: (message: WebSocketMessage) => void;
  subscribeMessage: (message: WebSocketMessage) => void;
  ordersViewing: { order_id: string; viewers: string[] }[];
  notifications: Notification[];
  clearNotification: (id: string) => void;
}

const WebSocketContext = createContext<WebSocketContextValue | undefined>(undefined);

interface WebSocketProviderProps {
  children: ReactNode;
}

function WebSocketProvider({ children }: Readonly <WebSocketProviderProps>) {
  const [ordersViewing, setOrdersViewing] = useState<{ order_id: string; viewers: string[] }[]>([]);
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const onMessage = useCallback((data: WebSocketMessage) => {
    if (data.type === "company_update") {
      setOrdersViewing(data.orders || []);
    }
    
    if (data.type === "system_notification") {
      setNotifications(prev => {
        const exists = prev.some(notification => notification.id === data.notification.id);
        if (exists) {
          return prev.map(notification => 
            notification.id === data.notification.id ? data.notification : notification
          );
        } else {
          return [...prev, data.notification];
        }
      });
    }

    if (data.type === "active_notifications") {
      setNotifications(data.notifications || []);
    }

    if (data.type === "notification_deleted") {
      setNotifications(prev => prev.filter(notification => notification.id !== data.notificationId));
    }

  }, []);

  const { isConnected, sendMessage, subscribeMessage } = useWebSocket({
    url: import.meta.env.VITE_WEBSOCKET_URL,
    onMessage,
  });

  useEffect(() => {
    if (isConnected) {
      subscribeMessage({
        action: "subscribe_notifications"
      });
    }
  }, [isConnected, subscribeMessage]);

  const clearNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  }, []);

  const value = useMemo(
    () => ({
      isConnected,
      sendMessage,
      subscribeMessage,
      ordersViewing,
      notifications,
      clearNotification,
    }),
    [isConnected, sendMessage, subscribeMessage, ordersViewing, notifications, clearNotification]
  );

  return <WebSocketContext.Provider value={value}>{children}</WebSocketContext.Provider>;
}

function useWebSocketContext() {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error("useWebSocketContext must be used within a WebSocketProvider");
  }
  return context;
}

export { WebSocketProvider, useWebSocketContext };
export type { Notification };